<script>
import Layout from "../../layouts/main";
import PerPage from "../../../components/per-page.vue";
import * as moment from "moment";

import {
  FETCH_AUDIT_LOG,
  FETCH_AUDIT_LOG_MASTER_MERCHANT,
  FETCH_MERCHANTS_LIST,
} from "@/state/actions.type";
import MonacoEditor from "monaco-editor-vue";
import Multiselect from "vue-multiselect";
import DaySelection from "../../../components/day-selection.vue";

export default {
  components: {
    Layout,
    PerPage,
    MonacoEditor,
    Multiselect,
    DaySelection,
  },
  data() {
    return {
      isFetching: true,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      auditLogs: [],
      totalRows: 0,
      fields: [
        { key: "action", label: "Type" },
        { key: "severity", label: "Severity" },
        {
          key: "description",
          label: "Description",
          thStyle: { width: "40vw" },
          tdClass: "wrap-description",
        },
        { key: "created_at", label: "Created", sortable: true },
        { key: "show_details", label: "" },
      ],
      from: "2018-01-01T00:00:00+05:30",
      to: "2025-08-19T23:59:59+05:30",
      sorting_data: {
        sort_by: "",
        sort_order: "",
      },
      showLogDetails: false,
      monacoOptions: {
        language: "json",
        theme: "vs-dark",
        readOnly: true,
        automaticLayout: true,
        lineNumbers: "on",
        minimap: { enabled: false },
        wordWrap: "on",
        scrollbar: {
          vertical: "hidden", // Hide vertical scrollbar
          horizontal: "hidden", // Hide horizontal scrollbar
          useShadows: false,
        },
        scrollBeyondLastLine: false,
      },
      jsonData: {},
      merchant_uuids: null,
      day: "Today",
      filters: {
        severity: "",
      },
      dayOptions: [
        "Today",
        "Yesterday",
        "This week",
        "Last week",
        "Last month",
        "This year",
        "All time",
      ],
    };
  },
  computed: {
    rows() {
      return this.$store.state.invoices.total_elements;
    },
    isMasterMerchant() {
      return this.$store.state.auth.currentUser.is_master_merchant;
    },
    merchantListDataFromStore() {
      return this.$store.state.masterMerchant.content;
    },
  },
  created() {
    this.from = new Date();
    this.from.setHours(0, 0, 0, 0);

    this.to = new Date();
    this.to.setHours(23, 59, 59, 999);

    this.fetchAuditLogs();

    if (this.isMasterMerchant) {
      this.$store.dispatch(`masterMerchant/${FETCH_MERCHANTS_LIST}`, {
        pageIndex: 1,
        pagesize: 10,
        filters: { source: "default" },
      });
    }
  },
  filters: {
    date(value) {
      return moment(value).format("DD/MM/YYYY");
    },
    time(value) {
      return "at " + moment(value).format("hh:mm:ss A");
    },
    logHeader(value) {
      if (!value) return "";
      // remove underscore and convert it to space and capitalize the first letter ex LOGIN_SUCCESS to Login Success
      return value
        .toLowerCase()
        .replace(/_/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
  },
  methods: {
    sortAuditLogs(item) {
      this.sorting_data.sort_by = item.sortBy;
      if (item.sortDesc) {
        this.sorting_data.sort_order = "desc";
      } else {
        this.sorting_data.sort_order = "asc";
      }
      this.fetchAuditLogs();
    },
    fetchAuditLogs() {
      this.isFetching = true;

      let payload = {
        from: this.from,
        to: this.to,
        page: this.currentPage,
        size: this.perPage,
        ...this.sorting_data,
        filters: this.filters,
      };
      if (!this.filters.severity) {
        delete payload.filters.severity;
      }

      let action = FETCH_AUDIT_LOG;

      if (this.isMasterMerchant) {
        action = FETCH_AUDIT_LOG_MASTER_MERCHANT;
        payload.merchant_keys = this.merchant_uuids
          ? [this.merchant_uuids.key]
          : [];
      }

      this.$store
        .dispatch(`merchant/${action}`, payload)
        .then((response) => {
          if (response.body && response.body.content) {
            this.auditLogs = response.body.content;
            this.totalRows = response.body.total_elements;
          } else {
            this.auditLogs = [];
            this.totalRows = 0;
          }
        })
        .catch((error) => {
          console.error("Error fetching audit logs:", error);
        })
        .finally(() => {
          this.isFetching = false;
        });
    },
    detailsClicked(item) {
      this.jsonData = item;
      this.showLogDetails = true;
    },
    hideDetails() {
      this.showLogDetails = false;
    },
    dayChanged(value) {
      this.day = value.day;
      this.from = value.startDate;
      this.to = value.endDate;
      this.fetchAuditLogs();
    },
  },
};
</script>

<template>
  <Layout>
    <div class="menu-content justify-content-start">
      <DaySelection
        :day="day"
        @day-selected="dayChanged"
        :dayOptions="dayOptions"
      />
      <multiselect
        v-model="filters.severity"
        :options="['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']"
        placeholder="Search Severity"
        class="sm-multiselect w-25"
        :searchable="true"
        :clearable="true"
        @input="fetchAuditLogs"
      >
        <template #clear>
          <span
            v-if="filters.severity"
            title="Clear"
            @click="
              filters.severity = '';
              fetchAuditLogs();
            "
            class="cursor-pointer multiselect__clear"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect width="24" height="24" fill="#fff"/>
              <path d="M7.24264 16.2426L15.7279 7.75736M15.7279 16.2426L7.24264 7.75736" stroke="black" stroke-opacity="0.5" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </template>
      </multiselect>

      <multiselect
        v-if="isMasterMerchant"
        v-model="merchant_uuids"
        :options="merchantListDataFromStore"
        :placeholder="$t('common.search_merchants')"
        track-by="email_id"
        label="email_id"
        class="sm-multiselect merchant_uuids w-25 ml-auto"
        :searchable="true"
        :clearable="true"
        @input="fetchAuditLogs"
      >
        <template #clear>
          <span
            v-if="merchant_uuids"
            title="Clear"
            @click="
              merchant_uuids = null;
              fetchAuditLogs();
            "
            class="cursor-pointer multiselect__clear"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect width="24" height="24" fill="#fff"/>
              <path d="M7.24264 16.2426L15.7279 7.75736M15.7279 16.2426L7.24264 7.75736" stroke="black" stroke-opacity="0.5" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </template>
      </multiselect>
    </div>

    <b-table
      class="table-custom"
      :items="auditLogs"
      :fields="fields"
      responsive="sm"
      @sort-changed="sortAuditLogs"
      sticky-header="50vh"
      style="height: 50vh;"
      show-empty
      no-local-sorting
      :busy="isFetching"
      :empty-text="$t('common.no-records')"
      hover
    >
      <template #table-busy>
        <b-spinner class="align-middle" :small="true"></b-spinner>
      </template>
      <template v-slot:head()="data">
        <span>
          {{ $t(data.label) }}
          <img
            v-show="data.field.sortable && sorting_data.sort_by !== data.column"
            class="ml-2"
            src="@/assets/images/arrow-3.svg"
            alt="sort_up"
          />
          <img
            v-show="
              data.field.sortable &&
                sorting_data.sort_by === data.column &&
                sorting_data.sort_order === 'asc'
            "
            class="ml-2"
            src="@/assets/images/sort_asc.svg"
            alt="sort_asc"
          />
          <img
            v-show="
              data.field.sortable &&
                sorting_data.sort_by === data.column &&
                sorting_data.sort_order === 'desc'
            "
            class="ml-2"
            src="@/assets/images/sort_desc.svg"
            alt="sort_desc"
          />
        </span>
      </template>
      <template #cell(created_at)="row">
        <p class="table-date">{{ row.value | date }}</p>
        <p class="table-time">{{ row.value | time }}</p>
      </template>

      <template #cell(show_details)="row">
        <b-btn
          @click="detailsClicked(row.item)"
          class="font-12px text-black"
          style="border-radius: 8px; border: 1px solid #DCDAD5; background: rgba(220, 218, 213, 0.18); padding: 6px 16px; font-weight: 500; line-height: 20px;"
          >Details</b-btn
        >
      </template>
    </b-table>

    <div class="d-flex justify-content-between">
      <per-page
        v-model="perPage"
        :currentPage="currentPage"
        :totalRows="totalRows"
        :tableRowsCount="auditLogs.length"
        @input="fetchAuditLogs"
      />
      <!-- invoicesDataFromStore.length -->
      <b-pagination
        class="my-auto"
        v-model="currentPage"
        :current-page="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        @input="fetchAuditLogs"
      ></b-pagination>
    </div>

    <b-modal
      v-model="showLogDetails"
      hide-footer
      hide-header
      size="lg"
      no-close-on-backdrop
      body-class="p-0"
      centered
    >
      <div style="padding: 2rem;">
        <h4 class="mb-3">{{ jsonData.action | logHeader }}</h4>
        <div
          class="json-editor p-3"
          style="color: #000; font-size: 18px; font-style: normal; font-weight: 700; line-height: 24px; /* 133.333% */"
          v-if="showLogDetails && jsonData"
        >
          <MonacoEditor
            :value="JSON.stringify(jsonData, null, 2)"
            :options="monacoOptions"
            style="height:400px;width:100%;"
          />
        </div>
        <b-btn
          variant="dark"
          class="d-block ml-auto mt-4 px-3 py-2"
          style="background-color: #000; border-radius: 8px; color: #FFF;"
          @click="hideDetails"
        >
          close
        </b-btn>
      </div>
    </b-modal>
  </Layout>
</template>

<style scoped>
.json-editor {
  background: #1e1e1e;
}
::v-deep .wrap-description {
  white-space: normal !important;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-width: 40vw;
}
::v-deep .merchant_uuids .multiselect__single {
  overflow-x: hidden;
}
::v-deep .multiselect__clear {
  opacity: 1 !important;
  z-index: 1;
  position: absolute;
  right: 15px;
  top: 12px;
  font-size: 18px;
  cursor: pointer;
}
</style>
